import {MenuItem} from '../../models/MenuItem';
import {ChangeStatusPage} from '../change-status/change-status';
import {ManageTagsPage} from '../manage-tags/manage-tags';
import {IdentifyTagPage} from '../identify-tag/identify-tag';
import {CycleCountPage} from '../cycle-count/cycle-count';
import {UnassociatedTagsPage} from '../unassociated-tags/unassociated-tags';
import {DeviceSelectPage} from '../device-select/device-select';
import {AppConfigPage} from '../app-config/app-config';
import {ConstantValues} from './menu-tracker-retail.constants';
import {
  Alert,
  AlertController,
  AlertOptions,
  Content,
  IonicPage,
  NavController,
  NavParams,
  Platform
} from 'ionic-angular';
import {Page} from 'ionic-angular/navigation/nav-util';
import {TranslateService} from '@ngx-translate/core';
import {ReaderService} from '../../providers/reader.service';
import {ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {VizixService} from '../../providers/vizix.service';
import {ReplenishmentPage} from '../replenishment/replenishment';
import {LoginPage} from '../login/login';
import {VizixEventService} from '../../providers/vizix.event.service';
import {LookUpPage} from '../look-up/look-up';
import {VizixEventItem} from '../../models/VizixEventItem';
import {SimulatorService} from '../../providers/simulator.service';
import {Storage} from '@ionic/storage';
import {ReplenishmentService} from '../../providers/replenishment.service';
import {EncodeTagsPage} from '../encode-tags/encode-tags';
import {MenuItemI} from '../../interfaces/menu-item.i';
import * as Constants from '../../app/app.constants';
import {Subscription} from 'rxjs/Subscription';
import {Observable} from 'rxjs/Observable';
import {Observer} from 'rxjs/Observer';
import 'rxjs/add/operator/finally';

@IonicPage()
@Component({
  selector: 'page-menu-tracker-retail',
  templateUrl: 'menu-tracker-retail.html',
})
export class MenuTrackerRetailPage {

  @ViewChild(Content) content: Content;
  private items: Array<MenuItem> = [];
  private alert: Alert = null;
  private subMenu: MenuItem;
  private isLogoutOptionClicked: boolean;
  private isLogoutInProgress: boolean = false;
  private logoutTimer: any;
  private isLoading: boolean = false;
  private isConnectedSubscription: Subscription;

  constructor(public navCtrl: NavController,
              private navParams: NavParams,
              public readerService: ReaderService,
              private platform: Platform,
              public vizixService: VizixService,
              public replenishmentService: ReplenishmentService,
              private simulatorService: SimulatorService,
              private storage: Storage,
              public translate: TranslateService,
              private alertCtrl: AlertController,
              private cdr: ChangeDetectorRef,
              private vizixEventService: VizixEventService) {
    this.initMenu();

  }

  /**
   * Life Cycle Hook
   * Runs when the page is about to enter and become the active page.
   */
  ionViewWillEnter() {
    this.stopReader();
  }

  /**
   * Life Cycle Hook
   * Runs when the page has loaded. This event only happens once per page being created.
   * If a page leaves but is cached, then this event will not fire again on a subsequent viewing.
   */
  ionViewDidLoad() {
    this.onBackToMenu();
    if (!this.subMenu) {
      this.getTotalReplenishmentTasks();
    }
  }

  /**
   * Life Cycle Hook
   * Runs when the page has fully entered and is now the active page.
   * This event will fire, whether it was the first load or a cached page.
   */
  ionViewDidEnter() {
    this.content.resize();
    this.registerBackButtonAction();
    this.setTotalTasks(this.replenishmentService.lastReplenishmentTaskNumber);

  }

  /**
   * Initialize Menu Navigation
   */
  initMenu() {

    /**
     * Set a Tree Model Hierarchy
     * @type {Array<MenuItemI>}
     */
    const items: Array<MenuItemI> = [
      {
        id: ConstantValues.ID_MENU_MANAGE_TAGS,
        icon: 'tr_icon_redesign_manageTags',
        text: 'menu_tracker_retail.manage_tags',
        order: 0,
        requiresReader: false,
        page: MenuTrackerRetailPage,
        items: [
          {
            id: ConstantValues.ID_MENU_SUB_MANAGE_TAGS,
            icon: 'tr_icon_redesign_manageTags',
            text: 'menu_tracker_retail.associate_tags',
            order: 0,
            requiresReader: false,
            page: ManageTagsPage
          },
          {
            id: ConstantValues.ID_MENU_SUB_CHANGE_STATUS,
            icon: 'tr_icon_redesign_changeStatus',
            text: 'menu_tracker_retail.change_status',
            order: 1,
            requiresReader: true,
            page: ChangeStatusPage
          }
          , {
            id: ConstantValues.ID_MENU_SUB_ENCODE_TAGS,
            icon: 'tr_icon_encode_tag',
            text: 'menu_tracker_retail.encode_tags',
            order: 2,
            requiresReader: true,
            page: EncodeTagsPage
          }
        ]
      },
      {
        id: ConstantValues.ID_MENU_CYCLE_COUNT,
        icon: 'tr_icon_redesign_cycleCount',
        text: 'menu_tracker_retail.cycle_count',
        order: 1,
        requiresReader: true,
        page: CycleCountPage,
      },
      {
        id: ConstantValues.ID_MENU_IDENTIFY_TAGS,
        icon: 'tr_icon_redesign_identifyTags',
        text: 'menu_tracker_retail.identify_tag',
        order: 2,
        requiresReader: false,
        page: IdentifyTagPage,
      },
      {
        id: ConstantValues.ID_MENU_LOCATE_ITEMS,
        icon: 'tr_icon_redesign_locateItems',
        text: 'menu_tracker_retail.locate_items',
        order: 3,
        requiresReader: false,
        page: MenuTrackerRetailPage,
        items: [
          {
            id: ConstantValues.ID_MENU_SUB_LOOKUP,
            icon: 'tr_icon_look_up',
            text: 'menu_tracker_retail.look_up',
            order: 0,
            requiresReader: false,
            page: LookUpPage
          },
          {
            id: ConstantValues.ID_MENU_SUB_UNASSOCIATED_TAGS,
            icon: 'tr_icon_redesign_unassociatedTags',
            text: 'menu_tracker_retail.unassociated_tags',
            order: 1,
            requiresReader: false,
            page: UnassociatedTagsPage
          }
        ]
      },
      {
        id: ConstantValues.ID_MENU_TASKS,
        icon: 'tr_icon_redesign_tasks',
        text: 'menu_tracker_retail.tasks',
        order: 4,
        requiresReader: false,
        count: 0,
        page: MenuTrackerRetailPage,
        requiresBadge: true,
        items: [
          {
            id: ConstantValues.ID_SUB_MENU_REPLENISHMENT,
            icon: 'tr_icon_redesign_replenishment',
            text: 'menu_tracker_retail.replenishment',
            order: 0,
            requiresReader: false,
            count: 0,
            page: ReplenishmentPage,
            requiresBadge: true
          }
        ]
      },
      {
        id: ConstantValues.ID_MENU_LOGOUT,
        icon: 'tr_icon_redesign_logout',
        text: 'menu_tracker_retail.logout',
        order: 5,
        requiresReader: false,
        page: LoginPage
      }
    ];

    /**
     * Recursive Function for traversing all tree nodes
     * @param {Array<MenuItemI>} items
     * @param {MenuItemI} target
     */
    const constructMenuItems = (items: Array<MenuItemI>, target?: MenuItem): void => {
      items.forEach((item: MenuItemI, index) => {
        if (item.hasOwnProperty('items') && item.items && Array.isArray(item.items) && item.items.length) {
          constructMenuItems(item.items, item);
        }
        if (target) {
          target.items[index] = new MenuItem(item);
        } else {
          this.items.push(new MenuItem(item));
        }
      });
    };

    /**
     * Construct Navigation Tree
     */
    const subMenu = this.navParams.get('item');
    if (subMenu) {
      this.subMenu = subMenu;
      this.items = this.subMenu.items;
    } else {
      constructMenuItems(items);
    }

  }

  /**
   * Register Back Button Actions
   */
  registerBackButtonAction() {
    this.platform.registerBackButtonAction(() => {
      if (this.alert instanceof Alert) {
        this.alert.dismiss();
      } else {
        if (this.subMenu) {
          this.goBack();
        } else {
          if (this.platform.is('android')) {
            this.alertExitOrLogoutConfirm();
          } else if (this.platform.is('ios')) {
            this.alertLogoutConfirm();
          }
        }
      }
    });
  }

  /**
   * Respond accordingly when menu is accessed with parameters
   */
  onBackToMenu() {
    switch (this.navParams.get(Constants.NAV_PARAM_RETURN_PAGE)) {
      case Constants.RETURN_PAGE_MANAGE_TAGS:
        this.goToPage(ManageTagsPage, false);
        break;
      case Constants.RETURN_PAGE_ENCODE_TAGS:
        this.goToPage(EncodeTagsPage, true);
        break;
      case Constants.RETURN_PAGE_CHANGE_STATUS:
        this.goToPage(ChangeStatusPage, true);
        break;
      case Constants.RETURN_PAGE_CYCLE_COUNT:
        this.goToPage(CycleCountPage, true);
        break;
      case Constants.RETURN_PAGE_IDENTIFY_TAG:
        this.goToPage(IdentifyTagPage, false);
        break;
      case Constants.RETURN_PAGE_LOOK_UP:
        this.goToPage(LookUpPage, false);
        break;
      case Constants.RETURN_PAGE_UNASSOCIATED_TAGS:
        this.goToPage(UnassociatedTagsPage, false);
        break;
      case Constants.RETURN_PAGE_REPLENISHMENT:
        this.goToPage(ReplenishmentPage, false);
        break;
    }
  }

  /**
   * Fetch replenishment pending tasks to show in badge
   */
  getTotalReplenishmentTasks() {
    if (!this.isLoading) {
      this.isLoading = true;
      this.replenishmentService.setReplenishmentArray()
        .finally(() => this.isLoading = false)
        .subscribe(() => this.setTotalTasks(this.replenishmentService.lastReplenishmentTaskNumber));
    }
  }

  /**
   * Update the badge count from replenishment tasks
   * @param {number} totalTasks
   */
  setTotalTasks(totalTasks: number) {
    const replenishmentMenuItem: MenuItem = this.items.find((item: MenuItem) => item.id === ConstantValues.ID_MENU_TASKS);
    let replenishmentMenuSubItem: MenuItem;
    if (replenishmentMenuItem) {
      replenishmentMenuItem.count = totalTasks;
      replenishmentMenuSubItem = replenishmentMenuItem.items.find((item: MenuItem) => item.id === ConstantValues.ID_SUB_MENU_REPLENISHMENT);
    } else {
      replenishmentMenuSubItem = this.items.find((item: MenuItem) => item.id === ConstantValues.ID_SUB_MENU_REPLENISHMENT);
    }
    if (replenishmentMenuSubItem) {
      replenishmentMenuSubItem.count = totalTasks;
    }
  }

  /**
   * Stop Reader operations
   */
  private stopReader() {
    this.readerService.stopScanCC().subscribe();
  }

  /**
   * Go to the configuration page
   */
  goToAppConfig() {
    this.navCtrl.push(AppConfigPage);
  }

  /**
   * Close the application on Android
   */
  private closeApp() {
    if (this.isLogoutOptionClicked) {
      this.recordEventAndLogout();
    }
    else {
      this.platform.exitApp();
    }
  }

  /**
   * Record Logout Event
   */
  private recordEventAndLogout() {

    const prepareVizixEventItem = (vizixEvent: VizixEventItem) => {
      let event = vizixEvent;
      event.action = Constants.VizixEventAction.NONE;
      event.category = Constants.VizixEventCategory.WORKFLOW;
      event.subCategory = Constants.VizixEventSubCategory.AUTHENTICATION;
      event.eventType = Constants.VizixEventEventType.INFO;
      event.eventId = Constants.VIZIX_EVENT_ID_LOGOUT;
      event.activity = Constants.VizixEventActivity.LOGOUT;
      return event;
    };

    this.vizixEventService.getVizixEventItemBase().then((vizixEvent: VizixEventItem) => {
      const event = prepareVizixEventItem(vizixEvent);
      this.vizixEventService.recordEvent(event).then(() => {
        clearTimeout(this.logoutTimer);
        this.logout();
      });
      this.logoutTimer = setTimeout(() => this.logout(), 100);
    })
      .catch(() => this.logout());
  }

  /**
   * Logout
   */
  private logout() {
    if (!this.isLogoutInProgress) {
      this.isLogoutInProgress = true;
      this.vizixService.VizixLogOut().subscribe(() => {
        this.vizixService.clearVizixService();
        this.storage.remove(Constants.STORAGE_READER_SOUND_LEVEL).then(() => {
          this.navCtrl.popToRoot({animate: false}).then(() => {
            this.navCtrl.push(LoginPage);
          });
        });
      });
    }
  }

  /**
   * When a menu item is tapped or clicked
   * @param {MenuItem} item
   */
  onClickItem(item: MenuItem) {
    switch (item.id) {
      case ConstantValues.ID_MENU_LOGOUT:
        if (this.platform.is('android')) {
          this.alertExitOrLogoutConfirm();
        }
        else {
          this.alertLogoutConfirm();
        }
        break;
      default:
        this.goToPage(item.page, item.requiresReader, item);
        break;
    }
  }

  /**
   * Exit or Logout Confirmation
   */
  private alertExitOrLogoutConfirm() {
    this.presentAlert({
      title: this.translate.instant('menu_tracker_retail.alerts.exit_or_logout.title'),
      subTitle: this.translate.instant('menu_tracker_retail.alerts.exit_or_logout.message'),
      buttons: [
        {
          text: this.translate.instant('menu_tracker_retail.alerts.exit_or_logout.logout'),
          handler: () => {
            this.isLogoutOptionClicked = true;
            this.setReaderMaxSound()
              .finally(() => this.readerService.disconnect().finally(() => this.closeApp()).subscribe())
              .subscribe();
          }
        },
        {
          text: this.translate.instant('menu_tracker_retail.alerts.exit_or_logout.exit'),
          handler: () => {
            this.isLogoutOptionClicked = false;
            this.setReaderMaxSound()
              .finally(() => this.readerService.disconnect().finally(() => this.closeApp()).subscribe())
              .subscribe();
          }
        },
        {
          text: this.translate.instant('menu_tracker_retail.alerts.exit_or_logout.cancel'),
          role: 'cancel'
        }
      ]
    });
  }

  /**
   * Logout Confirmation
   */
  private alertLogoutConfirm() {
    this.presentAlert({
      title: this.translate.instant('menu_tracker_retail.alerts.logout.title'),
      subTitle: this.translate.instant('menu_tracker_retail.alerts.logout.message'),
      buttons: [
        {
          text: this.translate.instant('menu_tracker_retail.alerts.logout.cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('menu_tracker_retail.alerts.logout.ok'),
          handler: () => {
            this.isLogoutOptionClicked = true;
            this.setReaderMaxSound()
              .finally(() => this.readerService.disconnect().finally(() => this.closeApp()).subscribe())
              .subscribe();
          }
        }
      ]
    });
  }

  /**
   * Handle presenting the alert and the latter dismissal of it setting the member's value to null
   */
  private presentAlert(options: AlertOptions) {
    this.alert = this.alertCtrl.create(options);
    this.alert.present();
    this.alert.onDidDismiss(() => this.alert = null);
  }

  /**
   * Set Reader Max Sound
   */
  setReaderMaxSound(): Observable<any> {
    return Observable.create((observer: Observer<any>) => {

      const maxSoundSet = (result: any = null) => {
        observer.next(result);
        observer.complete();
      };

      this.readerService.isConnected().subscribe(
        (result) => {
          if (result) {
            if (this.simulatorService.isSimulatorEnabled) {
              maxSoundSet(result);
            } else {
              const maxLevelSound = this.readerService.readerInformation.soundLevels - 1;
              this.readerService.setBeepSoundNoStorageValue(maxLevelSound)
                .finally(() => maxSoundSet(result))
                .subscribe();
            }
          } else {
            maxSoundSet(result);
          }
        },
        () => {
          maxSoundSet();
        }
      );
    });
  }

  /**
   * Validate requirements and redirect to page
   * @param {Page} page
   * @param {boolean} requiresReader
   * @param {MenuItem} item
   */
  private goToPage(page: Page, requiresReader: boolean, item?: MenuItem) {
    if (requiresReader) {
      const disableSimulator = page === EncodeTagsPage;
      if (this.isConnectedSubscription && !this.isConnectedSubscription.closed) {
        this.isConnectedSubscription.unsubscribe();
      }
      this.isConnectedSubscription = this.readerService.isConnected(disableSimulator).subscribe(
        (result) => {
          if (result) {
            this.navCtrl.push(page);
          } else {
            this.alertReaderNotConnected();
          }
        },
        () => this.alertReaderNotConnected()
      );
    } else {
      this.navCtrl.push(page, {item});
    }
  }

  /**
   * Alert that the reader is not connected
   */
  private alertReaderNotConnected() {
    this.presentAlert({
      title: this.translate.instant('menu_tracker_retail.alerts.error_pair.title'),
      message: this.translate.instant('menu_tracker_retail.alerts.error_pair.message'),
      buttons: [
        {
          text: this.translate.instant('menu_tracker_retail.alerts.error_pair.cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('menu_tracker_retail.alerts.error_pair.ok'),
          handler: () => {
            this.navCtrl.push(DeviceSelectPage, {backToMenu: true});
          }
        }
      ]
    });
  }

  /**
   * Handle Interface Back Button (iOS)
   */
  goBack() {
    if (this.subMenu) {
      this.navCtrl.pop();
    } else {
      this.alertLogoutConfirm();
    }
  }

  /**
   * Refresh Replenishment Tasks Count
   * @param {Event} event
   */
  refreshTasksCount(event: Event) {
    if (!this.isLoading) {
      event.stopPropagation();
      setTimeout(()=>{
        this.cdr.detectChanges();
        this.getTotalReplenishmentTasks();
      },200)

    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/menu-tracker-retail/menu-tracker-retail.ts